import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { ClickableTableCell, DashboardTable } from "./dashboard-table";
import { parseAssetRiskRatings } from "~/lib/arrow";
import { getColor } from "~/lib/colors";
import DashboardSkeleton from "~/pages/organization/dashboard/DashboardSkeleton";
import type { SpaceEdge } from "./types";

export type SpacesGlanceProps = {
  spaceEdges: SpaceEdge[];
  spacesLoading: boolean;
};

const barRatings = [
  "critical",
  "high",
  "medium",
  "low",
  "none",
  "pass",
] as const;

export function SpacesGlance({ spaceEdges, spacesLoading }: SpacesGlanceProps) {
  const theme = useTheme();

  if (spacesLoading) {
    return <DashboardSkeleton />;
  }

  const edges = spaceEdges;

  if (!edges.length) {
    return (
      <Typography
        sx={{
          fontWeight: "bold",
          color: "text.secondary",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        Create spaces in your organization, and a summary will be shown here
      </Typography>
    );
  }

  return (
    <DashboardTable>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: "40%" }}>Name</TableCell>
          <TableCell sx={{ width: "40%", p: 0 }}>Risk Distribution</TableCell>
          <TableCell sx={{ width: "20%" }}>Assets</TableCell>
        </TableRow>
      </TableHead>
      <TableBody sx={[{ ".MuiTableCell-body": { fontSize: 12 } }]}>
        {edges.map(({ node }) => {
          const spaceId = node.mrn.split("/").pop();
          const spaceHref = `/space/overview?spaceId=${spaceId}`;
          const entries =
            node.metrics?.__typename === "MetricsResponse"
              ? node.metrics.entries
              : [];
          const riskRatings = parseAssetRiskRatings(entries || []);

          return (
            <TableRow key={node.mrn}>
              <ClickableTableCell
                sx={{ width: "40%", maxWidth: "160px" }}
                href={spaceHref}
              >
                {node.name}
              </ClickableTableCell>
              <ClickableTableCell sx={{ width: "40%", p: 0 }} href={spaceHref}>
                <RiskBar data={riskRatings.context} total={riskRatings.total} />
              </ClickableTableCell>
              <ClickableTableCell sx={{ width: "20%" }} href={spaceHref}>
                {riskRatings.total}
              </ClickableTableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </DashboardTable>
  );
}

function RiskBar({
  data,
  total,
}: {
  data: Array<{ rating: string; value: number }>;
  total: number;
}) {
  const theme = useTheme();

  if (total === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        height: 8,
        borderRadius: 1,
        overflow: "hidden",
        width: "100%",
      }}
    >
      {barRatings.map((rating) => {
        const entry = data.find((d) => d.rating === rating);
        const value = entry?.value || 0;
        if (value === 0) return null;
        const pct = (value / total) * 100;
        return (
          <Box
            key={rating}
            sx={{
              width: `${pct}%`,
              backgroundColor: getColor(theme, rating),
              minWidth: pct > 0 ? 2 : 0,
            }}
          />
        );
      })}
    </Box>
  );
}
